import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAppointments } from "../../context/AppointmentsContext";
import { formatDateDisplay, formatMileage, getStatusColor } from "../useAppointmentsPage/formatters";

const DEFAULT_LIMIT = 5;

const buildSnapshotItem = (appointment) => {
  const vehicle = appointment.vehicle || {};
  const vehicleName =
    vehicle.modelYear && vehicle.make && vehicle.model
      ? `${vehicle.modelYear} ${vehicle.make} ${vehicle.model}`
      : vehicle.model
      ? `${vehicle.make || ""} ${vehicle.model}`.trim()
      : vehicle.make || "—";

  const serviceMileage = formatMileage(vehicle.serviceMileage);
  const tags = [
    {
      text: appointment.statusDescription || appointment.statusCode || "—",
      color: getStatusColor(appointment.statusCode),
    },
  ];

  const createdVia = [
    vehicle.createdVia || "",
    formatDateDisplay(vehicle.createdDate, "MMM DD, YYYY"),
  ]
    .filter(Boolean)
    .join(" · ");

  const details = [
    {
      label: "Date and Time",
      value: formatDateDisplay(appointment.appointmentDate, "MMM DD, YYYY · hh:mm A") || "—",
    },
    {
      label: "Service Mileage",
      value: serviceMileage || "—",
    },
    {
      label: "Advisor",
      value: appointment.advisorName || "—",
    },
  ];

  return {
    id: appointment.appointmentUuid,
    vehicle: vehicleName,
    createdVia,
    tags,
    details,
  };
};

export const useAppointmentsSnapshot = (limit = DEFAULT_LIMIT) => {
  const { contactId } = useParams();
  const {
    baseAppointments,
    baseMeta,
    basePartyUuid,
    loading,
    error,
    isEmpty,
    fetchAppointments,
    setFilteredAppointments,
    resetAppointments,
  } = useAppointments();

  const hasAttemptedLoadRef = useRef(false);

  useEffect(() => {
    if (
      contactId &&
      basePartyUuid &&
      basePartyUuid !== contactId
    ) {
      resetAppointments();
    }
    hasAttemptedLoadRef.current = false;
  }, [contactId, basePartyUuid, resetAppointments]);

  const loadBase = useCallback(() => {
    if (!contactId) return;
    hasAttemptedLoadRef.current = true;
    fetchAppointments(
      {
        party_uuid: contactId,
        page: 1,
        page_size: 20,
        order_by: ["createdAt"],
        order_direction: ["DESC"],
      },
      { persistAsBase: true }
    );
  }, [contactId, fetchAppointments]);

  useEffect(() => {
    if (!contactId) return;

    if (baseAppointments.length) {
      hasAttemptedLoadRef.current = true;
      setFilteredAppointments(baseAppointments, baseMeta);
      return;
    }

    if (hasAttemptedLoadRef.current) {
      return;
    }

    loadBase();
  }, [
    contactId,
    baseAppointments.length,
    baseAppointments,
    baseMeta,
    loadBase,
    setFilteredAppointments,
  ]);

  const snapshotLoading = loading && !baseAppointments.length;

  const snapshotAppointments = useMemo(
    () => baseAppointments.slice(0, limit).map(buildSnapshotItem),
    [baseAppointments, limit]
  );

  return {
    contactId,
    loading: snapshotLoading,
    error,
    isEmpty,
    meta: baseMeta,
    appointments: snapshotAppointments,
    refetch: loadBase,
  };
};

